import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useAppSelector } from '../../../../store/hooks';
import { useGetQuizQuery, usePostQuizGuessMutation } from '../../api/artOfTheDayApi';
import type { QuizBoardDto, QuizPick } from '../../api/quizTypes';
import { ViewToggle } from '../../components/shared/RaceView';
import GuessBox from './GuessBox';
import GuessLine from './GuessLine';
import GuessTable from './GuessTable';
import PrizeCard from './PrizeCard';
import { useIsNarrow } from '../../../emojis/components/emojiCatalogShared';
import { checkRole } from '../../../../components/recipe_page/recipe_page';
import {
  belongsToArt,
  boardIsStale,
  boardView,
  formatClock,
  formatMinutes,
  guessErrorText,
  guessViews,
  initialPicks,
  lastLock,
  playsQuiz,
  QUIZ_ANCHOR_ID,
  QUIZ_LENGTH_MS,
  SLOTS_PER_ROW,
} from './quizModel';
import { QUIZ_COLORS } from './Tiles';

/**
 * The emoji quiz under the race graph. The board is `GET /art-of-the-day/quiz`
 * (artOfTheDayApi.getQuiz); other players' locks arrive over the race socket
 * (`api/quizSocket.ts`). Renders nothing until today's quiz exists, and only
 * when it belongs to the art on the page. Every signed-in member plays;
 * signed-out visitors watch the line without a box.
 */

const Wrap = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  margin-top: 14px;
`;

const Header = styled.div`
  font-size: 0.75rem;
  font-style: italic;
  color: ${QUIZ_COLORS.label};
  margin-bottom: 4px;
  padding-right: 64px;
`;

const Burn = styled.div`
  height: 2px;
  background: #f3f4f6;
  margin-bottom: 10px;
  overflow: hidden;
`;

const Watching = styled.div`
  font-size: 0.72rem;
  font-style: italic;
  color: ${QUIZ_COLORS.label};
  text-align: center;
`;

/** While the clock reads zero but the server hasn't closed the quiz yet. */
const CLOSING_POLL_MS = 5_000;
/** A slow backstop under the socket while the quiz runs. */
const OPEN_POLL_MS = 60_000;

function watchingLine(board: QuizBoardDto): string | null {
  if (board.status !== 'open' || board.you.eligible) return null;
  return board.you.reason === 'signed_out'
    ? "members are guessing today's emojis. sign in to play."
    : "members are guessing today's emojis.";
}

/** `author`: who made the art on the page (the board must be its quiz). */
const AotdEmojiQuiz: FC<{ author?: string }> = ({ author }) => {
  const reduce = useReducedMotion();
  const auth = useAppSelector((s) => s.auth);
  const ready = auth.bootstrapStatus === 'ready';
  const signedIn = ready && Boolean(auth.token);
  const isAdmin = signedIn && checkRole(auth.role || '', 'admin');
  const narrow = useIsNarrow();

  const [pollMs, setPollMs] = useState(0);
  const { data, fulfilledTimeStamp, refetch, isUninitialized } = useGetQuizQuery(undefined, {
    // wait for the session, so a member's first read already says what they can do
    skip: !ready,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: pollMs,
  });
  // signing in or out on this page changes the viewer part of the board
  const lastSignedIn = useRef(signedIn);
  useEffect(() => {
    if (lastSignedIn.current === signedIn) return;
    lastSignedIn.current = signedIn;
    if (!isUninitialized) void refetch();
  }, [signedIn, isUninitialized, refetch]);

  const [postGuess, { isLoading: locking }] = usePostQuizGuessMutation();

  const dto = data?.quiz && belongsToArt(data.quiz, author) ? data.quiz : null;
  const board = useMemo(() => (dto ? boardView(dto) : null), [dto]);
  const guesses = useMemo(() => (dto ? guessViews(dto) : []), [dto]);

  // server time minus local time, so the clock agrees with the close task
  const skew = useMemo(() => {
    const server = data?.serverNow ? Date.parse(data.serverNow) : NaN;
    return Number.isFinite(server) && fulfilledTimeStamp ? server - fulfilledTimeStamp : 0;
  }, [data?.serverNow, fulfilledTimeStamp]);

  const opensAt = dto ? Date.parse(dto.opensAt) : 0;
  const closesAt = dto ? Date.parse(dto.closesAt) : 0;
  const windowMs = dto && closesAt > opensAt ? closesAt - opensAt : QUIZ_LENGTH_MS;
  const open = dto?.status === 'open';

  const [now, setNow] = useState(() => Date.now() + skew);
  useEffect(() => {
    setNow(Date.now() + skew);
    if (!open) return undefined;
    const t = setInterval(() => setNow(Date.now() + skew), 1000);
    return () => clearInterval(t);
  }, [open, skew]);
  const remaining = Math.max(0, closesAt - now);
  const timeUp = open && remaining <= 0;
  useEffect(() => {
    setPollMs(timeUp ? CLOSING_POLL_MS : open ? OPEN_POLL_MS : 0);
  }, [open, timeUp]);

  // The box starts from your latest lock, else a heist draft, else blanks, and
  // starts over when the board changes shape (a heist restart adds a row) or
  // you locked again (here or in another tab), so a locked box always shows
  // the picks the server holds.
  const shape = dto ? `${dto.dayKey}|${dto.opensAt}|${dto.rows.length}|${dto.you.attemptsUsed}` : '';
  const [picks, setPicks] = useState<QuizPick[]>([]);
  const shapeRef = useRef('');
  useEffect(() => {
    if (!dto || shapeRef.current === shape) return;
    shapeRef.current = shape;
    setPicks(initialPicks(dto));
  }, [dto, shape]);

  const [advanced, setAdvanced] = useState(false);
  const [rewinding, setRewinding] = useState(false);

  if (!dto || !board) return null;

  const you = dto.you;
  const closed = dto.status === 'closed';
  const elapsed = Math.min(windowMs, Math.max(0, now - opensAt));
  const winner = dto.winner;
  const me = auth.username;
  const who = (name: string) => (me && name === me ? 'you' : name);

  let status: string;
  if (closed && dto.endedBy === 'perfect' && winner) status = `${who(winner.username)} got it at ${formatMinutes(winner.atMs)}, game over`;
  else if (closed) status = winner ? `closed, ${who(winner.username)} won with ${winner.score} pts` : 'closed';
  else if (timeUp) status = "time's up, adding up the scores";
  else status = `closes in ${formatClock(remaining)}`;

  const playing = playsQuiz(you);
  // past closesAt the server refuses locks before the close task runs
  const tooLate = timeUp || (!you.canGuess && you.reason === 'closed');
  const watching = watchingLine(dto);

  const lock = async (): Promise<string | null> => {
    // always the board's full length: four per row, blanks as null
    const sent = Array.from({ length: dto.rows.length * SLOTS_PER_ROW }, (_, i) => picks[i] ?? null);
    try {
      await postGuess({ dayKey: dto.dayKey, picks: sent }).unwrap();
      return null;
    } catch (err) {
      // a refusal that says the board is out of date: read it again now
      if (boardIsStale(err)) void refetch();
      return guessErrorText(err);
    }
  };

  const dir = advanced ? 1 : -1;
  return (
    <Wrap id={QUIZ_ANCHOR_ID}>
      <ViewToggle
        type="button"
        onClick={() => (advanced ? setRewinding(true) : setAdvanced(true))}
        disabled={rewinding}
        aria-pressed={advanced}
        style={{ top: 1 }}
      >
        {advanced ? 'traditional' : 'advanced'}
      </ViewToggle>
      <Header>
        <span>guess the emojis</span>{' '}
        <span style={{ color: closed ? QUIZ_COLORS.ink2 : QUIZ_COLORS.brick }}>· {status}</span>
      </Header>
      <Burn>
        {!closed && (
          <div style={{ height: '100%', width: `${(remaining / windowMs) * 100}%`, background: QUIZ_COLORS.brick, transition: 'width 1s linear' }} />
        )}
      </Burn>

      {closed || playing ? (
        <GuessBox
          board={board}
          picks={picks}
          onPicksChange={setPicks}
          lockedAtMs={lastLock(dto)}
          onLock={lock}
          busy={locking || tooLate}
          closed={closed}
          prize={closed && dto.prize && winner ? { winner: who(winner.username), card: dto.prize } : null}
          reopened={Boolean(you.draft) && you.attemptsUsed === 0}
          secondTry={you.canGuess && you.attemptsAllowed > 1 && you.attemptsUsed === 1}
          timeUp={!closed && tooLate}
        />
      ) : watching ? (
        <Watching>{watching}</Watching>
      ) : null}

      {!closed && dto.prizePreview ? (
        <PrizeCard dayKey={dto.dayKey} prize={dto.prizePreview} wide={!narrow} isAdmin={isAdmin} />
      ) : null}

      <motion.div layout={!reduce} transition={{ duration: 0.3, ease: 'easeOut' }} style={{ overflow: 'hidden', marginTop: 8 }}>
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={advanced ? 'table' : 'line'}
            initial={reduce ? { opacity: 0 } : { opacity: 0, x: 40 * dir }}
            animate={{ opacity: 1, x: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, x: -40 * dir }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            {advanced ? (
              <GuessTable
                board={board}
                guesses={guesses}
                closed={closed}
                rewind={rewinding}
                onRewound={() => {
                  setRewinding(false);
                  setAdvanced(false);
                }}
              />
            ) : (
              <GuessLine board={board} guesses={guesses} elapsedMs={elapsed} closed={closed} />
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </Wrap>
  );
};

export default AotdEmojiQuiz;
